'use client';

import { useState } from 'react';
//import { Heading, BodyBase, } from './typography';
import Image from 'next/image';
import { usePostHog } from 'posthog-js/react';
import { useUser } from '../providers/UserProvider';
import { usePathname } from 'next/navigation';
import { HelpRequest } from '../api/help/route';
import { Button, BodyHeading, BodySmall, BodyBase } from '@puzzlio/ui';

interface ContactFormProps {
  onClose: () => void;
}

export default function ContactForm({ onClose }: ContactFormProps) {
  const user = useUser();
  const [email, setEmail] = useState(user?.auth?.email ?? '');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const posthog = usePostHog();
  const pathname = usePathname();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);
    setError('');

    const helpRequest: HelpRequest = {
      message,
      email,
      user: {
        user_str: user?.user_str || '-not found-',
        posthog_id: posthog.get_distinct_id(),
        session_replay_url: posthog.get_session_replay_url(),
        // Build URL from window.location to avoid useSearchParams and a Suspense boundary requirement.
        current_url: pathname + window.location.search,
      },
    };

    try {
      const response = await fetch('/api/help', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(helpRequest),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send message');
      }

      setSent(true);
      setMessage('');
      setEmail('');
    } catch (err) {
      console.error('Contact form error:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-[rgba(0,0,0,0.5)]"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-labelledby="contact-form-title"
    >
      <div
        className="border-coral bg-cream relative w-[calc(100%-32px)] max-w-md overflow-hidden rounded-[20px] border-[6px] shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)] sm:w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-[30px] pb-2">
          <div id="contact-form-title" className="flex-1">
            <BodyHeading className="mb-4 text-black">Contact Us</BodyHeading>

            <BodySmall className="text-gray-600">
              Questions about Inkwell? Stuck on your game? Just want to say hi?
            </BodySmall>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-500 transition-colors hover:text-gray-700"
            aria-label="Close contact form"
          >
            <Image src="/close.svg" alt="Close" width={24} height={24} />
          </button>
        </div>

        {sent ? (
          <div className="p-[30px] pt-2 text-center">
            <BodyBase className="text-coral mb-2">Message sent successfully!</BodyBase>
            <BodySmall className="mb-4 text-gray-600">We'll get back to you via email soon.</BodySmall>
            <Button onClick={onClose} variant="flat" size="flat" className="rounded md:hidden">
              Close
            </Button>
            <Button onClick={onClose} variant="gameplay" size="gameplay" color="red" className="hidden md:inline-flex">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-[30px] pt-2">
            <label className="font-body mb-3 flex items-center gap-2 text-sm sm:text-base">
              From:
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="focus:ring-coral min-w-0 flex-1 rounded-md border p-2 focus:ring-2 focus:outline-hidden sm:p-3"
                required
              />
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="How can we help? We'll get back to you via email."
              className="font-body focus:ring-coral h-32 w-full resize-none rounded-md border p-2 text-sm focus:ring-2 focus:outline-hidden sm:p-3 sm:text-base"
              disabled={isSending}
              required
            />
            {error && <BodySmall className="mt-2 text-red-600">{error}</BodySmall>}
            <div className="mt-4">
              <Button
                type="submit"
                variant="gameplay"
                size="gameplay"
                color="red"
                disabled={isSending || !message.trim() || !email.trim()}
                className="md:hidden"
              >
                {isSending ? 'Sending...' : 'Send Message'}
              </Button>
              <Button
                type="submit"
                variant="gameplay"
                size="gameplay"
                color="red"
                disabled={isSending || !message.trim() || !email.trim()}
                className="hidden md:inline-flex"
              >
                {isSending ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
